import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Calendar, Sparkles, Loader2, RefreshCw, ChevronDown, Printer, Mail, AlertTriangle, TrendingUp, TrendingDown, Minus, Download, Send, Share2, Link as LinkIcon, Copy, Check } from "lucide-react";
import { api, API, getToken } from "../lib/api";
import Layout from "../components/Layout";
import AuthImage from "../components/AuthImage";
import { MUSCLE_BY_ID, canonicalMuscleId } from "../data/muscleAtlas";
import { toast } from "sonner";

/**
 * SessionCompare — Iter AC
 *
 * `/clients/:clientId/compare?from=&to=` — side-by-side diff of two sessions
 * for the same client. Quick presets at the top let the therapist pick
 * Baseline (first ↔ current), Previous Visit (last ↔ current), or Custom.
 *
 * Layout (top → bottom):
 *   1. Header (client name, two dates, days-between, presets, share buttons)
 *   2. Improvement Score banner (overall + Pain/ROM/Findings sub-bars)
 *   3. AI "What Changed" narrative (4 sections + headline + patient_message)
 *   4. Photo strip (cardinal + movement, side-by-side)
 *   5. Pain delta card
 *   6. ROM delta table (handles all 3 modes)
 *   7. Findings + Worked-areas diff
 */
export default function SessionCompare() {
  const { id: clientId } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const fromId = params.get("from");
  const toId = params.get("to");
  const [preset, setPreset] = useState(params.get("preset") || "baseline");

  const [client, setClient] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [diff, setDiff] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const [narrative, setNarrative] = useState(null);
  const [narrativeBusy, setNarrativeBusy] = useState(false);
  const [narrativeError, setNarrativeError] = useState("");

  // Email modal state (Sprint 2)
  const [showEmail, setShowEmail] = useState(false);
  // Share modal state (Sprint 3)
  const [showShare, setShowShare] = useState(false);

  // Load client + session list (oldest first for preset math)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [c, sList] = await Promise.all([
          api.get(`/clients/${clientId}`),
          api.get(`/sessions/by-client/${clientId}`),
        ]);
        if (!alive) return;
        setClient(c.data);
        const sorted = (sList.data || []).slice().sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
        setSessions(sorted);
        // If query params missing, derive from preset (default = baseline)
        if (!fromId || !toId) {
          const derived = derivePreset(preset || "baseline", sorted);
          if (derived) {
            setParams({ from: derived.from, to: derived.to, preset: preset || "baseline" }, { replace: true });
          } else {
            setError("This client doesn't have two completed sessions to compare yet.");
          }
        }
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "Could not load comparison.");
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  // Fetch diff when both ids are set
  useEffect(() => {
    let alive = true;
    setNarrative(null); // reset narrative when pair changes
    if (!fromId || !toId) return;
    setBusy(true); setError("");
    api.get(`/session-compare?from=${fromId}&to=${toId}`)
      .then((r) => { if (alive) setDiff(r.data); })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || "Could not compute the comparison."); })
      .finally(() => { if (alive) setBusy(false); });
    return () => { alive = false; };
  }, [fromId, toId]);

  const applyPreset = (p) => {
    setPreset(p);
    const derived = derivePreset(p, sessions);
    if (derived) {
      setParams({ from: derived.from, to: derived.to, preset: p }, { replace: true });
    }
  };

  const generateNarrative = async (force = false) => {
    if (!fromId || !toId) return;
    setNarrativeBusy(true); setNarrativeError("");
    try {
      const r = await api.post("/session-compare/narrative", {
        from_session_id: fromId,
        to_session_id: toId,
        force_regenerate: force,
      });
      setNarrative(r.data);
    } catch (e) {
      setNarrativeError(e?.response?.data?.detail || "Could not generate narrative.");
    } finally {
      setNarrativeBusy(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10" data-testid="session-compare-page">
        {/* Header */}
        <div className="mb-6">
          <Link
            to={`/clients/${clientId}`}
            className="inline-flex items-center gap-1.5 text-sm text-ink-500 hover:text-ink-900 mb-3"
            data-testid="compare-back-link"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to {client?.name || "client"}
          </Link>
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="label-overline text-sage-600 mb-1">Session comparison</div>
              <h1 className="font-heading text-3xl sm:text-4xl font-medium text-ink-900">
                {client?.name || "Client"} — Before / After
              </h1>
              {diff && (
                <p className="text-sm text-ink-700 mt-1" data-testid="compare-header-meta">
                  <span className="font-medium text-ink-900">{formatDate(diff.from_session.created_at)}</span>
                  {" → "}
                  <span className="font-medium text-ink-900">{formatDate(diff.to_session.created_at)}</span>
                  {diff.days_between != null && <span className="text-ink-500"> · {diff.days_between} day{diff.days_between === 1 ? "" : "s"} apart</span>}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2 print:hidden">
              <button
                type="button"
                onClick={() => window.print()}
                data-testid="compare-print-btn"
                className="min-h-[40px] px-3 rounded-full border border-paper-200 text-xs text-ink-700 hover:bg-paper-50 inline-flex items-center gap-1.5"
              >
                <Printer className="w-3.5 h-3.5" /> Print
              </button>
              {fromId && toId && (
                <a
                  href={`${API}/session-compare/report.pdf?from=${fromId}&to=${toId}&auth=${getToken()}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="compare-pdf-btn"
                  className="min-h-[40px] px-3 rounded-full border border-paper-200 text-xs text-ink-700 hover:bg-paper-50 inline-flex items-center gap-1.5"
                >
                  <Download className="w-3.5 h-3.5" /> PDF
                </a>
              )}
              {fromId && toId && (
                <button
                  type="button"
                  onClick={() => setShowShare(true)}
                  data-testid="compare-share-btn"
                  className="min-h-[40px] px-3 rounded-full bg-purple-700 hover:bg-purple-800 text-white text-xs font-medium inline-flex items-center gap-1.5"
                >
                  <Share2 className="w-3.5 h-3.5" /> Share
                </button>
              )}
              {fromId && toId && (
                <button
                  type="button"
                  onClick={() => setShowEmail(true)}
                  data-testid="compare-email-btn"
                  className="min-h-[40px] px-3 rounded-full bg-sage-500 hover:bg-sage-600 text-white text-xs font-medium inline-flex items-center gap-1.5"
                >
                  <Mail className="w-3.5 h-3.5" /> Email
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Preset chips */}
        <div className="mb-6 flex flex-wrap gap-2" data-testid="compare-presets">
          <PresetChip
            active={preset === "baseline"}
            onClick={() => applyPreset("baseline")}
            label="Baseline (first ↔ current)"
            testid="compare-preset-baseline"
          />
          <PresetChip
            active={preset === "previous"}
            onClick={() => applyPreset("previous")}
            label="Previous visit"
            testid="compare-preset-previous"
            disabled={sessions.length < 2}
          />
          <CustomPicker
            sessions={sessions}
            fromId={fromId}
            toId={toId}
            onApply={(f, t) => { setPreset("custom"); setParams({ from: f, to: t, preset: "custom" }, { replace: true }); }}
            testid="compare-custom"
          />
        </div>

        {error && (
          <div className="rounded-2xl bg-rose-50 border border-rose-200 p-4 text-sm text-rose-900 flex gap-2" data-testid="compare-error">
            <AlertTriangle className="w-4 h-4 mt-0.5" /> {error}
          </div>
        )}

        {busy && !diff && (
          <div className="rounded-3xl bg-white border border-paper-200 p-10 text-center text-ink-500">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
            Computing comparison…
          </div>
        )}

        {diff && (
          <>
            {/* Improvement Score banner */}
            <ImprovementBanner score={diff.improvement_score} />

            {/* AI narrative */}
            <NarrativeBlock
              narrative={narrative}
              busy={narrativeBusy}
              error={narrativeError}
              onGenerate={() => generateNarrative(false)}
              onRegen={() => generateNarrative(true)}
            />

            {/* Photos */}
            <PhotoStrip pairs={diff.photos} fromDate={diff.from_session.created_at} toDate={diff.to_session.created_at} />

            {/* Pain */}
            <PainCard pain={diff.pain} />

            {/* ROM */}
            <RomTable rom={diff.rom} />

            {/* Findings + worked areas */}
            <FindingsBlock findings={diff.findings} />
          </>
        )}

        {/* Email modal */}
        {showEmail && fromId && toId && (
          <EmailCompareModal
            fromId={fromId}
            toId={toId}
            client={client}
            onClose={() => setShowEmail(false)}
          />
        )}

        {/* Share modal */}
        {showShare && fromId && toId && (
          <ShareCompareModal
            fromId={fromId}
            toId={toId}
            client={client}
            onClose={() => setShowShare(false)}
          />
        )}
      </div>
    </Layout>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────

function derivePreset(p, sessions) {
  const completed = sessions.filter((s) => (s.status || "completed") === "completed");
  if (completed.length < 2) return null;
  const first = completed[0];
  const last = completed[completed.length - 1];
  const prev = completed[completed.length - 2];
  if (p === "previous") {
    return { from: prev.id, to: last.id };
  }
  // default baseline
  return { from: first.id, to: last.id };
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
  } catch { return iso; }
}

function muscleLabel(mid) {
  const m = MUSCLE_BY_ID[mid] || MUSCLE_BY_ID[canonicalMuscleId(mid)];
  return m?.label || mid;
}

// ── Sub-components ────────────────────────────────────────────────────────

function PresetChip({ active, onClick, label, testid, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      data-active={active}
      className={`min-h-[40px] px-4 rounded-full text-sm font-medium inline-flex items-center gap-1.5 border transition-colors disabled:opacity-40 ${
        active
          ? "bg-ink-900 text-white border-ink-900"
          : "bg-white text-ink-700 border-paper-200 hover:border-ink-900/30"
      }`}
      aria-pressed={active}
    >
      {label}
    </button>
  );
}

function CustomPicker({ sessions, fromId, toId, onApply, testid }) {
  const [open, setOpen] = useState(false);
  const [a, setA] = useState(fromId || "");
  const [b, setB] = useState(toId || "");
  useEffect(() => { setA(fromId || ""); setB(toId || ""); }, [fromId, toId]);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`${testid}-trigger`}
        className="min-h-[40px] px-4 rounded-full text-sm font-medium inline-flex items-center gap-1.5 border bg-white text-ink-700 border-paper-200 hover:border-ink-900/30"
      >
        Custom comparison <ChevronDown className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div
          className="absolute z-20 mt-1 w-[min(28rem,calc(100vw-2rem))] bg-white border border-paper-200 rounded-2xl shadow-lg p-3"
          data-testid={`${testid}-panel`}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
            <div>
              <div className="label-overline mb-1">From</div>
              <select value={a} onChange={(e) => setA(e.target.value)} className="w-full min-h-[40px] px-2 rounded-xl bg-paper-50 border border-paper-200 text-sm" data-testid={`${testid}-from`}>
                <option value="">—</option>
                {sessions.map((s) => (
                  <option key={s.id} value={s.id}>{formatDate(s.created_at)}{s.imported_from_history ? " (imported)" : ""}</option>
                ))}
              </select>
            </div>
            <div>
              <div className="label-overline mb-1">To</div>
              <select value={b} onChange={(e) => setB(e.target.value)} className="w-full min-h-[40px] px-2 rounded-xl bg-paper-50 border border-paper-200 text-sm" data-testid={`${testid}-to`}>
                <option value="">—</option>
                {sessions.map((s) => (
                  <option key={s.id} value={s.id}>{formatDate(s.created_at)}{s.imported_from_history ? " (imported)" : ""}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setOpen(false)} className="min-h-[36px] px-3 rounded-full text-sm text-ink-600">Cancel</button>
            <button
              type="button"
              data-testid={`${testid}-apply`}
              onClick={() => { if (a && b && a !== b) { onApply(a, b); setOpen(false); } else { toast.error("Pick two different sessions."); } }}
              className="min-h-[36px] px-4 rounded-full bg-ink-900 hover:bg-ink-700 text-white text-sm font-medium"
            >
              Compare
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ImprovementBanner({ score }) {
  const overall = score?.overall;
  const signed = score?.overall_signed;
  const tone = overall == null ? "neutral" : overall >= 60 ? "positive" : overall <= 40 ? "negative" : "neutral";
  const toneClass = {
    positive: "from-sage-500 to-sage-600 text-white",
    neutral:  "from-paper-100 to-paper-50 text-ink-900",
    negative: "from-rose-500 to-rose-600 text-white",
  }[tone];
  return (
    <div
      className={`rounded-3xl p-6 sm:p-7 mb-6 bg-gradient-to-br ${toneClass}`}
      data-testid="compare-improvement-banner"
    >
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="label-overline opacity-80">Overall progress</div>
          <div className="font-heading text-4xl sm:text-5xl font-medium mt-1" data-testid="compare-improvement-overall">
            {overall == null ? "—" : <>
              {signed != null && signed >= 0 ? "+" : ""}{signed != null ? signed : overall}<span className="text-2xl opacity-70">%</span>
            </>}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4 sm:gap-6">
          <SubScore label="Pain" pct={score?.pain} signed={score?.pain_signed} testid="compare-score-pain" tone={tone} />
          <SubScore label="ROM" pct={score?.rom} signed={score?.rom_signed} testid="compare-score-rom" tone={tone} />
          <SubScore label="Findings" pct={score?.findings} signed={score?.findings_signed} testid="compare-score-findings" tone={tone} />
        </div>
      </div>
    </div>
  );
}

function SubScore({ label, pct, signed, testid, tone }) {
  const muted = tone === "neutral";
  return (
    <div className="min-w-[80px]" data-testid={testid}>
      <div className={`text-[10px] uppercase tracking-wider ${muted ? "text-ink-500" : "opacity-80"}`}>{label}</div>
      <div className="font-heading text-2xl font-medium mt-0.5">
        {pct == null ? "—" : <>{signed != null && signed >= 0 ? "+" : ""}{signed != null ? signed : pct}<span className="text-base opacity-70">%</span></>}
      </div>
    </div>
  );
}

function NarrativeBlock({ narrative, busy, error, onGenerate, onRegen }) {
  return (
    <section
      className="rounded-3xl bg-white border border-paper-200 p-5 sm:p-6 mb-6"
      data-testid="compare-narrative"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-purple-100 flex items-center justify-center shrink-0">
            <Sparkles className="w-4 h-4 text-purple-700" />
          </div>
          <div>
            <div className="font-heading text-xl text-ink-900 flex items-center gap-2">
              What changed
              <span className="text-[9px] uppercase tracking-wider bg-purple-100 text-purple-800 px-1.5 py-0.5 rounded-full font-bold">AI</span>
            </div>
            <p className="text-xs text-ink-700 mt-0.5">Therapist-to-patient narrative anchored to the structured diff below.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!narrative && (
            <button
              type="button"
              onClick={onGenerate}
              disabled={busy}
              data-testid="compare-narrative-generate-btn"
              className="min-h-[40px] px-4 rounded-full bg-purple-700 hover:bg-purple-800 text-white text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {busy ? "Reading the diff…" : "Generate"}
            </button>
          )}
          {narrative && (
            <button
              type="button"
              onClick={onRegen}
              disabled={busy}
              data-testid="compare-narrative-regen-btn"
              className="min-h-[36px] px-3 rounded-full border border-paper-200 text-xs text-ink-700 hover:bg-paper-50 inline-flex items-center gap-1"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Refresh
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-2xl bg-rose-50 border border-rose-200 p-3 text-sm text-rose-900" data-testid="compare-narrative-error">
          {error}
        </div>
      )}

      {narrative && (
        <div className="space-y-4" data-testid="compare-narrative-result">
          {narrative.headline && (
            <p className="text-lg font-medium text-ink-900 leading-snug" data-testid="compare-narrative-headline">{narrative.headline}</p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <NarrSection title="Most meaningful improvements" items={narrative.improvements} tone="sage" testid="compare-narrative-improvements" />
            <NarrSection title="Areas still limited" items={narrative.still_limited} tone="amber" testid="compare-narrative-still-limited" />
          </div>
          {narrative.interpretation && (
            <div className="rounded-2xl bg-paper-50 border border-paper-200 p-4 text-sm text-ink-900 leading-relaxed" data-testid="compare-narrative-interpretation">
              <div className="label-overline mb-1">Clinical interpretation</div>
              {narrative.interpretation}
            </div>
          )}
          <NarrSection title="Recommended next focus" items={narrative.recommended_next_focus} tone="ink" testid="compare-narrative-next-focus" />
          {narrative.patient_message && (
            <div className="rounded-2xl bg-sage-50 border border-sage-200 p-4" data-testid="compare-narrative-patient-message">
              <div className="label-overline mb-1 text-sage-700">Patient message (copy-ready)</div>
              <p className="text-sm text-ink-900 italic">&ldquo;{narrative.patient_message}&rdquo;</p>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function NarrSection({ title, items, tone, testid }) {
  if (!items || items.length === 0) return null;
  const toneClass = {
    sage:  "border-sage-200 bg-sage-50/50",
    amber: "border-amber-200 bg-amber-50/60",
    rose:  "border-rose-200 bg-rose-50/60",
    ink:   "border-paper-200 bg-white",
  }[tone] || "border-paper-200 bg-white";
  return (
    <div className={`rounded-2xl border p-4 ${toneClass}`} data-testid={testid}>
      <div className="label-overline mb-2">{title}</div>
      <ul className="space-y-1 list-disc list-inside text-sm text-ink-900 leading-relaxed">
        {items.map((x, i) => <li key={i}>{x}</li>)}
      </ul>
    </div>
  );
}

function PhotoStrip({ pairs, fromDate, toDate }) {
  if (!pairs || pairs.length === 0) {
    return (
      <section className="rounded-3xl bg-white border border-paper-200 p-6 mb-6" data-testid="compare-photos-empty">
        <div className="label-overline mb-2">Photos</div>
        <p className="text-sm text-ink-500">No photos attached to either session yet. Upload baseline + post-treatment photos on each session to populate this strip.</p>
      </section>
    );
  }
  return (
    <section className="rounded-3xl bg-white border border-paper-200 p-5 sm:p-6 mb-6" data-testid="compare-photos">
      <div className="flex items-baseline justify-between gap-2 mb-4 flex-wrap">
        <h2 className="font-heading text-xl text-ink-900">Photos</h2>
        <div className="text-xs text-ink-500 grid grid-cols-2 gap-6 sm:gap-12">
          <div>From: <span className="text-ink-900 font-medium">{formatDate(fromDate)}</span></div>
          <div>To: <span className="text-ink-900 font-medium">{formatDate(toDate)}</span></div>
        </div>
      </div>
      <div className="space-y-4">
        {pairs.map((pair) => (
          <div key={`${pair.kind}-${pair.view}`} className="grid grid-cols-[110px,1fr,1fr] gap-3 sm:gap-4 items-center" data-testid={`compare-photo-row-${pair.view}`}>
            <div className="text-xs">
              <div className="font-medium text-ink-900">{pair.label}</div>
              <div className="text-[10px] uppercase tracking-wider text-ink-500">{pair.kind}</div>
            </div>
            <PhotoCell path={pair.a_path} testid={`compare-photo-${pair.view}-from`} />
            <PhotoCell path={pair.b_path} testid={`compare-photo-${pair.view}-to`} />
          </div>
        ))}
      </div>
    </section>
  );
}

function PhotoCell({ path, testid }) {
  if (!path) {
    return (
      <div className="rounded-xl bg-paper-50 border border-dashed border-paper-300 h-32 sm:h-40 flex items-center justify-center text-xs text-ink-500" data-testid={testid}>
        No photo
      </div>
    );
  }
  return (
    <AuthImage path={path} alt="" className="w-full h-32 sm:h-40 object-cover rounded-xl" testid={testid} />
  );
}

function PainCard({ pain }) {
  const Icon = pain?.score > 0 ? TrendingDown : pain?.score < 0 ? TrendingUp : Minus;
  const iconTone = pain?.score > 0 ? "text-sage-700" : pain?.score < 0 ? "text-rose-700" : "text-ink-500";
  return (
    <section className="rounded-3xl bg-white border border-paper-200 p-5 sm:p-6 mb-6" data-testid="compare-pain">
      <div className="flex items-start justify-between gap-3 mb-3">
        <h2 className="font-heading text-xl text-ink-900">Pain</h2>
        <Icon className={`w-5 h-5 ${iconTone}`} />
      </div>
      <div className="grid grid-cols-3 gap-3 text-center">
        <PainColumn label="From" before={pain?.a_before} after={pain?.a_after} />
        <div className="flex items-center justify-center text-2xl text-ink-500">→</div>
        <PainColumn label="To" before={pain?.b_before} after={pain?.b_after} />
      </div>
      {pain?.delta_text && (
        <p className="text-center text-sm mt-4">
          <span className={`px-3 py-1 rounded-full font-medium ${pain.score > 0 ? "bg-sage-100 text-sage-800" : pain.score < 0 ? "bg-rose-100 text-rose-800" : "bg-paper-100 text-ink-700"}`}>
            {pain.delta_text}
            {pain.delta_pct != null && ` (${pain.delta_pct > 0 ? "+" : ""}${pain.delta_pct}%)`}
          </span>
        </p>
      )}
    </section>
  );
}

function PainColumn({ label, before, after }) {
  return (
    <div>
      <div className="label-overline">{label}</div>
      <div className="text-3xl font-heading font-medium text-ink-900 mt-1">{after != null ? after : before != null ? before : "—"}</div>
      {before != null && after != null && (
        <div className="text-xs text-ink-500 mt-1">{before} → {after}</div>
      )}
    </div>
  );
}

function RomTable({ rom }) {
  const rows = rom?.rows || [];
  if (rows.length === 0) {
    return (
      <section className="rounded-3xl bg-white border border-paper-200 p-6 mb-6" data-testid="compare-rom-empty">
        <div className="label-overline mb-2">Range of motion</div>
        <p className="text-sm text-ink-500">No ROM measurements captured on either session.</p>
      </section>
    );
  }
  return (
    <section className="rounded-3xl bg-white border border-paper-200 p-5 sm:p-6 mb-6" data-testid="compare-rom">
      <h2 className="font-heading text-xl text-ink-900 mb-4">Range of motion</h2>
      <div className="space-y-2">
        {rows.map((r, i) => (
          <RomDiffRow key={i} row={r} index={i} />
        ))}
      </div>
    </section>
  );
}

function RomDiffRow({ row, index }) {
  const tone = row.improved ? "sage" : row.worsened ? "rose" : "neutral";
  const toneClass = {
    sage:    "bg-sage-50 border-sage-200",
    rose:    "bg-rose-50 border-rose-200",
    neutral: "bg-paper-50 border-paper-200",
  }[tone];
  const Icon = row.improved ? TrendingUp : row.worsened ? TrendingDown : Minus;
  const iconTone = row.improved ? "text-sage-700" : row.worsened ? "text-rose-700" : "text-ink-500";
  return (
    <div className={`rounded-2xl border p-3 sm:p-4 ${toneClass}`} data-testid={`compare-rom-row-${index}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${iconTone}`} />
          <div className="font-medium text-ink-900">{row.label}</div>
          <span className="text-[10px] uppercase tracking-wider bg-white text-ink-600 px-1.5 py-0.5 rounded-full border border-paper-200">{row.mode}</span>
        </div>
        {row.delta_text && (
          <div className="text-sm font-medium text-ink-900">{row.delta_text}{row.delta_pct != null && ` · ${row.delta_pct > 0 ? "+" : ""}${row.delta_pct}%`}</div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3 mt-2 text-xs text-ink-700">
        <RomSide side="From" entry={row.a} mode={row.mode} />
        <RomSide side="To" entry={row.b} mode={row.mode} />
      </div>
    </div>
  );
}

function RomSide({ side, entry, mode }) {
  const e = entry || {};
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-ink-500">{side}</div>
      {mode === "degree" && (
        <div className="text-ink-900">{e.before != null ? `${e.before}°` : "—"}{e.after != null ? ` → ${e.after}°` : ""}{e.target ? ` (target ${e.target}°)` : ""}</div>
      )}
      {mode === "restriction" && (
        <div className="text-ink-900 capitalize">{e.quality_before || "—"}{e.quality_after ? ` → ${e.quality_after}` : ""}</div>
      )}
      {mode === "comparison" && (
        <div className="text-ink-900 capitalize">{e.comparison || "—"}</div>
      )}
    </div>
  );
}

function FindingsBlock({ findings }) {
  const { resolved = [], new: newer = [], persisting = [], worked = {} } = findings || {};
  return (
    <section className="rounded-3xl bg-white border border-paper-200 p-5 sm:p-6 mb-10" data-testid="compare-findings">
      <h2 className="font-heading text-xl text-ink-900 mb-4">Findings &amp; muscles worked</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <DiffColumn label="Resolved" items={resolved} tone="sage" testid="compare-findings-resolved" />
        <DiffColumn label="New" items={newer} tone="rose" testid="compare-findings-new" />
        <DiffColumn label="Still present" items={persisting} tone="amber" testid="compare-findings-persisting" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <MuscleColumn label="Worked then only" items={(worked.only_a || []).map(muscleLabel)} testid="compare-worked-only-a" />
        <MuscleColumn label="Worked both visits" items={(worked.shared || []).map(muscleLabel)} testid="compare-worked-shared" />
        <MuscleColumn label="Worked now only" items={(worked.only_b || []).map(muscleLabel)} testid="compare-worked-only-b" />
      </div>
    </section>
  );
}

function DiffColumn({ label, items, tone, testid }) {
  const toneClass = {
    sage:  "border-sage-200 bg-sage-50/50",
    amber: "border-amber-200 bg-amber-50/60",
    rose:  "border-rose-200 bg-rose-50/60",
  }[tone] || "border-paper-200 bg-paper-50";
  return (
    <div className={`rounded-2xl border p-4 ${toneClass}`} data-testid={testid}>
      <div className="label-overline mb-2">{label} ({items.length})</div>
      {items.length === 0 ? (
        <p className="text-xs text-ink-500">None.</p>
      ) : (
        <ul className="space-y-1 list-disc list-inside text-sm text-ink-900">
          {items.slice(0, 8).map((x, i) => <li key={i}>{x}</li>)}
        </ul>
      )}
    </div>
  );
}

function MuscleColumn({ label, items, testid }) {
  return (
    <div className="rounded-2xl border border-paper-200 bg-paper-50 p-4" data-testid={testid}>
      <div className="label-overline mb-2">{label} ({items.length})</div>
      {items.length === 0 ? (
        <p className="text-xs text-ink-500">None.</p>
      ) : (
        <div className="flex flex-wrap gap-1">
          {items.slice(0, 16).map((x, i) => <span key={i} className="text-[11px] bg-white border border-paper-200 rounded-full px-2 py-0.5 text-ink-700">{x}</span>)}
        </div>
      )}
    </div>
  );
}

function EmailCompareModal({ fromId, toId, client, onClose }) {
  const [recipient, setRecipient] = useState(client?.email || "");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const send = async () => {
    if (!recipient) { toast.error("Recipient email is required."); return; }
    setBusy(true); setError("");
    try {
      await api.post("/session-compare/email-report", {
        from_session_id: fromId,
        to_session_id: toId,
        recipient_email: recipient,
        message: message.trim() || null,
      });
      toast.success(`Sent to ${recipient}.`);
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail === "email_not_configured") {
        // Build a mailto with the PDF URL as the fallback so the therapist
        // can still deliver the report.
        const url = `${API}/session-compare/report.pdf?from=${fromId}&to=${toId}&auth=${getToken()}`;
        const subject = encodeURIComponent(`Posturra Progress Comparison — ${client?.name || ""}`);
        const body = encodeURIComponent((message || "") + `\n\nDownload your comparison report: ${url}\n`);
        window.open(`mailto:${recipient}?subject=${subject}&body=${body}`, "_blank");
        toast.info("Email service not configured — opened your mail app.");
        onClose();
      } else {
        setError(detail || "Could not send email.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="compare-email-modal">
      <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-xl">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="label-overline mb-1">Send comparison</div>
            <h3 className="font-heading text-xl text-ink-900">Email this progress report</h3>
          </div>
          <button type="button" onClick={onClose} className="w-8 h-8 rounded-full hover:bg-paper-100 inline-flex items-center justify-center text-ink-500">×</button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="label-overline block mb-1">Recipient email</label>
            <input
              type="email"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              placeholder="patient@example.com"
              data-testid="compare-email-recipient"
              className="w-full min-h-[44px] px-3 rounded-xl bg-paper-50 border border-paper-200 outline-none text-sm"
            />
          </div>
          <div>
            <label className="label-overline block mb-1">Personal message (optional)</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              placeholder="Hi! Wanted to share your progress since our first visit…"
              data-testid="compare-email-message"
              className="w-full px-3 py-2 rounded-xl bg-paper-50 border border-paper-200 outline-none text-sm resize-none"
            />
          </div>
          {error && (
            <div className="rounded-xl bg-rose-50 border border-rose-200 p-2 text-xs text-rose-900" data-testid="compare-email-error">{error}</div>
          )}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="min-h-[40px] px-4 rounded-full text-sm text-ink-600 hover:bg-paper-100">Cancel</button>
          <button
            type="button"
            onClick={send}
            disabled={busy || !recipient}
            data-testid="compare-email-send-btn"
            className="min-h-[40px] px-5 rounded-full bg-sage-500 hover:bg-sage-600 text-white text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {busy ? "Sending…" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}


function ShareCompareModal({ fromId, toId, client, onClose }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [link, setLink] = useState(null);
  const [copied, setCopied] = useState(false);
  const [message, setMessage] = useState("");
  const [view, setView] = useState({ photos: true, scores: true, ai_summary: true, booking: true });
  const [ttlDays, setTtlDays] = useState(30);

  const create = async () => {
    setBusy(true); setError("");
    try {
      const r = await api.post("/portal/comparison", {
        from_session_id: fromId,
        to_session_id: toId,
        therapist_message: message.trim() || null,
        view_settings: view,
        ttl_days: ttlDays,
      });
      setLink(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not create share link.");
    } finally { setBusy(false); }
  };

  const fullUrl = link ? `${window.location.origin}${link.public_path}` : "";
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch { toast.error("Copy failed"); }
  };

  const emailViaMailto = () => {
    const recipient = client?.email || "";
    const subject = encodeURIComponent(`Your progress with ${(link?.branding?.therapist_name) || "Posturra"}`);
    const body = encodeURIComponent(`Hi,\n\nHere's your progress report:\n\n${fullUrl}\n\nThe link is valid until ${new Date(link.expires_at).toLocaleDateString()}.\n`);
    window.open(`mailto:${recipient}?subject=${subject}&body=${body}`, "_blank");
  };

  return (
    <div className="fixed inset-0 z-50 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="compare-share-modal">
      <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-xl">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="label-overline mb-1">Share progress</div>
            <h3 className="font-heading text-xl text-ink-900">Send {client?.name || "this client"} a private link</h3>
          </div>
          <button type="button" onClick={onClose} className="w-8 h-8 rounded-full hover:bg-paper-100 inline-flex items-center justify-center text-ink-500">×</button>
        </div>

        {!link ? (
          <>
            <div className="space-y-3 mb-4">
              <div>
                <div className="label-overline mb-1.5">Patient view includes</div>
                <div className="space-y-1.5">
                  <Toggle label="Photos (before / after)" value={view.photos} onChange={(v) => setView((x) => ({ ...x, photos: v }))} testid="share-toggle-photos" />
                  <Toggle label="Progress scores (overall, pain, range, findings)" value={view.scores} onChange={(v) => setView((x) => ({ ...x, scores: v }))} testid="share-toggle-scores" />
                  <Toggle label="AI progress summary" value={view.ai_summary} onChange={(v) => setView((x) => ({ ...x, ai_summary: v }))} testid="share-toggle-ai" />
                  <Toggle label="Booking link" value={view.booking} onChange={(v) => setView((x) => ({ ...x, booking: v }))} testid="share-toggle-booking" />
                </div>
              </div>
              <div>
                <label className="label-overline block mb-1">Personal note (optional)</label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={3}
                  placeholder="So proud of how far you've come!"
                  data-testid="share-message"
                  className="w-full px-3 py-2 rounded-xl bg-paper-50 border border-paper-200 outline-none text-sm resize-none"
                />
              </div>
              <div>
                <label className="label-overline block mb-1">Expires after</label>
                <select
                  value={ttlDays}
                  onChange={(e) => setTtlDays(parseInt(e.target.value, 10))}
                  data-testid="share-ttl"
                  className="w-full min-h-[40px] px-3 rounded-xl bg-paper-50 border border-paper-200 outline-none text-sm"
                >
                  <option value={7}>7 days</option>
                  <option value={14}>14 days</option>
                  <option value={30}>30 days</option>
                  <option value={60}>60 days</option>
                  <option value={90}>90 days</option>
                </select>
              </div>
              {error && (
                <div className="rounded-xl bg-rose-50 border border-rose-200 p-2 text-xs text-rose-900" data-testid="share-error">{error}</div>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={onClose} className="min-h-[40px] px-4 rounded-full text-sm text-ink-600 hover:bg-paper-100">Cancel</button>
              <button
                type="button"
                onClick={create}
                disabled={busy}
                data-testid="share-create-btn"
                className="min-h-[40px] px-5 rounded-full bg-purple-700 hover:bg-purple-800 text-white text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-50"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                {busy ? "Generating…" : "Create link"}
              </button>
            </div>
          </>
        ) : (
          <div className="space-y-3" data-testid="share-link-result">
            <p className="text-sm text-ink-700">Your private progress link is ready. It expires {new Date(link.expires_at).toLocaleDateString()}.</p>
            <div className="rounded-xl bg-paper-50 border border-paper-200 p-2 flex items-center gap-2">
              <LinkIcon className="w-3.5 h-3.5 text-ink-500 shrink-0" />
              <input value={fullUrl} readOnly className="flex-1 min-w-0 bg-transparent outline-none text-xs text-ink-900" data-testid="share-link-input" />
              <button
                type="button"
                onClick={copy}
                data-testid="share-copy-btn"
                className="min-h-[32px] px-2.5 rounded-lg bg-white border border-paper-200 text-xs text-ink-700 hover:border-ink-900/30 inline-flex items-center gap-1"
              >
                {copied ? <Check className="w-3 h-3 text-sage-700" /> : <Copy className="w-3 h-3" />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
              <button
                type="button"
                onClick={emailViaMailto}
                data-testid="share-email-btn"
                className="min-h-[40px] px-4 rounded-full bg-sage-500 hover:bg-sage-600 text-white text-sm font-medium inline-flex items-center gap-1.5"
              >
                <Mail className="w-3.5 h-3.5" /> Email link
              </button>
              <a
                href={fullUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="share-preview-link"
                className="min-h-[40px] px-4 rounded-full border border-paper-200 text-ink-700 hover:bg-paper-50 text-sm font-medium inline-flex items-center gap-1.5"
              >
                Preview <ArrowRight className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Toggle({ label, value, onChange, testid }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer select-none">
      <input
        type="checkbox"
        checked={!!value}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testid}
        className="w-4 h-4 rounded border-paper-300"
      />
      <span className="text-sm text-ink-900">{label}</span>
    </label>
  );
}

